/**
 * 派发到「员工自己的电脑」上执行的企业任务，一次执行的**产品面结论**。
 *
 * 它填的是一个具体的空：device 腿（管理员派一件事到员工机上，以员工本人身份跑）今天
 * 回报给管理员的只有 `status: success|failed` 加一段自由文本 `errorMessage`。管理员问
 * 「这批任务里哪些是因为员工没登录目标网站、哪些是被限流、哪些是浏览器压根没开」，
 * 一个字段都答不出——只能人眼读三十段中文。本枚举就是那个可聚合的判别式。
 *
 * ── 与仓内四套相邻词表的分工（别把本表当第五个工程分类器）────────────────────
 *   · `AgentTurnFailureClass`（types/index.ts）
 *       服务端**重试策略**的输入（哪些错值得再试一次）；员工任务页只把它翻成一句说明，不按它聚合。
 *   · `CronFailureCategory`（5 项，core/src/cron/failure-guidance.ts:14）
 *       对自由文本做**正则匹配**后产出通知文案的建议桶。是猜测，不是事实。
 *   · `CronDependencyIssue.reason`（3 项，core/src/cron/dependency-preflight.ts:29）
 *       只描述**跑之前**的依赖缺口，不描述跑完之后发生了什么。
 *   · `EnterpriseTaskExecution.resultSubtype`（自由串，types/index.ts:7228）
 *       SDK 结果 subtype 原样透传，词表由上游 SDK 决定，我方不定义。
 *
 * 本枚举的定位与上述四者都不同：它是**这一次 device 执行到底算什么结果**的产品结论，
 * 由派发器按「预检 → turn 事实 → agent 自报」三层证据推导，落库后供管理员按类聚合。
 *
 * ── 为什么单独开文件而不写进 `types/index.ts` ─────────────────────────────────
 * `types/index.ts` 是多棵活树同时在改的冲突热点。词表 + guard 自成一体、零依赖，
 * 独立文件既避开合并冲突，也让「唯一真源」这件事在文件粒度上可见：任何新增成员
 * 必须动这一个数组，服务端 guard 与客户端推导都从它派生（见同目录 .test.ts 的契约格）。
 */
/**
 * 唯一真源。顺序 = 从「成了」到「越来越不是我们能修的」，仅为可读性，无语义依赖。
 *
 * - `ok`                 — 真的干成了（有工具动作、无错误终局）。
 * - `partial`            — 只完成了一部分：脚本形态任务里大部分条目交上了、有几条没做成（N-159：
 *                          名下 10 场妙记交了 9 场、1 场没有导出权限）。**只有脚本腿的写入方**：脚本以
 *                          约定退出码 `DEVICE_SCRIPT_PARTIAL_EXIT_CODE` 退出，派发器 `deviceScriptOutcome`
 *                          记成它，原因文案开头是交了多少、没成多少。与 `page_failed` 分开，是因为一条
 *                          长期取不到的条目会让这个人连续很多轮都是失败，把他其实交了的大部分盖住；
 *                          agent 自报不得写它（一轮会话没有「条目」可数）。
 * - `not_logged_in`      — 登录态缺失。**两种来源共用一个码**：DOSIA 自身凭证过期
 *                          （turn 的 `authFailed`），或 agent 自报「目标网站要求登录」。
 *                          管理员的下一步动作是同一个：找这位员工去登录一次。
 * - `browser_not_ready`  — 任务要操作浏览器，但本机浏览器连接没就绪（跑之前就判定）。
 * - `browser_disabled`   — 「浏览器自动化」总开关被员工在设置里关掉了。
 * - `rate_limited`       — 目标站限流/风控退避（agent 自报）。
 * - `captcha`            — 目标站要求人工验证码（agent 自报）。
 * - `page_failed`        — 页面/流程没走通，含「一个工具都没调的空跑」。
 * - `timeout`            — 超出本次执行预算被终止。
 * - `permission_denied`  — 缺连接器/授权，本机做不了这件事。
 * - `device_offline`     — 员工电脑在截止前没回报（关机 / 断网）。**本枚举里唯一没有客户端
 *                          写入方的成员**：机器离线时它自己报不了，由服务端掉线检测写
 *                          （apps/web 的 `markOverdueDeviceExecutions`，随 status=overdue
 *                          一起写；电脑之后补报会整体取代它）。派发器的推导路径永不产出它
 *                          （有测试钉住）。
 * - `client_outdated`    — 这台电脑上的 DOSIA 版本低于脚本形态任务的最低版本
 *                          （`DEVICE_SCRIPT_TASK_MIN_APP_VERSION`），这一轮没在它上面执行（N-159）。
 *                          与 `device_offline` 一样**只有服务端写入方**：版本不够的电脑来轮询
 *                          `/tasks/assigned` 时由服务端建这条终态行；同一账号达到版本的电脑之后
 *                          来领，服务端把它恢复成 queued。派发器的推导路径永不产出它。
 */
export const ENTERPRISE_TASK_OUTCOMES = [
    "ok",
    "partial",
    "not_logged_in",
    "browser_not_ready",
    "browser_disabled",
    "rate_limited",
    "captcha",
    "page_failed",
    "timeout",
    "permission_denied",
    "device_offline",
    "client_outdated",
];
const OUTCOME_SET = new Set(ENTERPRISE_TASK_OUTCOMES);
/**
 * 反序列化边界 guard（红线 #7：解析外部输入先运行时校验，不用 `as` 顶替）。
 * 三处消费方共用它：员工端解析 agent 自报的结论行、服务端 `/complete` 收 body、
 * 以及任何读回历史执行记录的地方。
 */
export function isEnterpriseTaskOutcome(value) {
    return typeof value === "string" && OUTCOME_SET.has(value);
}
//# sourceMappingURL=enterprise-task-outcome.js.map